import { Box, createStyles, Input, Modal, TFNotifier, Tooltip, useDisclosure } from '@tf/ui';
import { isValidEmail } from '@tf/utils';
import React from 'react';
import { ModalFooter } from '@/components/shared';
import { useUpdateCaseClientEmailMutation } from '@/core/api/caseRequests';

const useStyles = createStyles(() => ({
	button: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		fontWeight: 600,
		borderBottom: '1px dotted',
		color: '#155EEF',
		cursor: 'pointer',
	},
	tooltipContent: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
}));

export const ClientEmail: React.FC<{
	caseId: string;
	email?: string;
	unsentRequestIds?: string[];
	isEditable: boolean;
}> = ({ caseId, email, unsentRequestIds, isEditable }) => {
	const { close, isOpen, toggle } = useDisclosure();
	const { classes } = useStyles();

	const updateCaseClientEmailMutation = useUpdateCaseClientEmailMutation();
	const hasEmail = !!email?.trim();

	const [fieldsState, setFieldsState] = React.useState({
		email: email ?? '',
		emailError: '',
	});

	const updateEmail = () => {
		if (!isValidEmail(fieldsState.email)) {
			setFieldsState((prevState) => ({ ...prevState, emailError: 'Invalid email' }));
			return;
		}

		updateCaseClientEmailMutation.mutate(
			{
				caseId,
				email: fieldsState.email,
				unsentRequestIds,
			},
			{
				onSuccess: () => {
					TFNotifier.success('Email updated');
					close();
				},
			},
		);
	};

	if (!isEditable) {
		return (
			<Tooltip label={email} position="top">
				<Box className={classes.tooltipContent}>{email}</Box>
			</Tooltip>
		);
	}

	return (
		<>
			<Modal title={hasEmail ? 'Update email' : 'Add email'} opened={isOpen} withinPortal onClose={toggle}>
				<form
					onSubmit={async (e) => {
						e.preventDefault();
						updateEmail();
					}}
				>
					<Input.Wrapper p="1rem" required label="Email">
						<Input
							error={fieldsState.emailError}
							placeholder="Enter email"
							value={fieldsState.email}
							onChange={(e) => {
								const email = e.target.value;
								setFieldsState(() => ({ email, emailError: '' }));
							}}
						/>
					</Input.Wrapper>
					<ModalFooter
						onSubmit={updateEmail}
						submitText="Update"
						isDisabled={updateCaseClientEmailMutation.isPending}
						onCancel={close}
					/>
				</form>
			</Modal>

			{hasEmail ? (
				<Tooltip label={email} position="top">
					<Box onClick={toggle} className={classes.button}>
						{email}
					</Box>
				</Tooltip>
			) : (
				<Box onClick={toggle} className={classes.button}>
					Add email
				</Box>
			)}
		</>
	);
};
